/**
 * DateChangePendingBanner — F-S2.
 *
 * Amber banner shown on the project detail page whenever one or more date change requests are
 * awaiting leadership approval. Renders nothing when there are no pending requests.
 */
import { CalendarClock } from 'lucide-react';

import type { DateChangeRequest } from '@/types';
import { dateFieldLabel, formatDateValue } from '@/lib/date-change';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export interface DateChangePendingBannerProps {
  requests: DateChangeRequest[];
}

export function DateChangePendingBanner({
  requests,
}: DateChangePendingBannerProps): JSX.Element | null {
  if (!requests || requests.length === 0) return null;

  return (
    <Alert className="border-amber-300/70 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
      <CalendarClock className="h-4 w-4" aria-hidden="true" />
      <AlertTitle>
        Date change pending approval
        {requests.length > 1 ? ` (${requests.length})` : ''}
      </AlertTitle>
      <AlertDescription>
        <ul className="mt-1 space-y-1">
          {requests.map((req) => (
            <li key={req.id}>
              <span className="font-medium">{dateFieldLabel(req.date_field)}</span>
              {' → '}
              <span className="font-medium">{formatDateValue(req.requested_value)}</span>
              <span className="text-amber-800/80 dark:text-amber-200/70">
                {' '}
                (requested by {req.requested_by})
              </span>
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}

export default DateChangePendingBanner;
